import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin, api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { lazyChunk } from '../lib/lazyChunk.js'
import { Loading } from './states.jsx'

const SpotsMap = lazyChunk(() => import('../components/SpotsMap.jsx'))

/** "Where should the next feeder go?" — pins on the map, votes, volunteers' status. */
export default function SpotsPage() {
  const { t } = useTranslation()
  const { user, isAdmin } = useAuth()
  const spots = useApi(() => api.spots(), [user?.uid])
  const feeders = useApi(() => api.feeders())
  const [draft, setDraft] = useState(null)
  const [note, setNote] = useState('')
  const [selected, setSelected] = useState(null)
  const [state, setState] = useState({ busy: false, error: null, info: null })

  const submit = async (e) => {
    e.preventDefault()
    setState({ busy: true, error: null, info: null })
    try {
      const r = await api.suggestSpot({ ...draft, note: note.trim() })
      setDraft(null)
      setNote('')
      setSelected(r.id)
      setState({ busy: false, error: null, info: r.merged ? t('spots.merged') : t('spots.added') })
      spots.reload()
    } catch (err) {
      setState({ busy: false, error: err.status === 429 ? t('spots.tooMany') : err.status === 422 ? t('spots.outside') : err.message, info: null })
    }
  }
  const toggleVote = async (s) => {
    await (s.voted ? api.unvoteSpot(s.id) : api.voteSpot(s.id))
    spots.reload()
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('spots.title')}</h1>
        <p className="max-w-3xl text-lg text-muted">{t('spots.lead')}</p>
      </div>
      <div className="flex flex-wrap items-stretch gap-6">
        <div className="min-h-[420px] min-w-0 flex-[999_1_560px]">
          <Suspense fallback={<Loading />}>
            <SpotsMap
              spots={spots.data ?? []}
              feeders={feeders.data ?? []}
              draft={draft}
              onPick={(p) => (user ? setDraft(p) : setState({ busy: false, error: null, info: t('spots.loginFirst') }))}
              onSelect={setSelected}
            />
          </Suspense>
        </div>
        <aside className="flex flex-[1_1_320px] flex-col gap-4">
          {draft ? (
            <form onSubmit={submit} className="flex flex-col gap-3 rounded-3xl bg-paper p-5">
              <span className="font-extrabold">📍 {t('spots.newHere')}</span>
              <textarea
                required
                minLength={5}
                maxLength={500}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('spots.notePlaceholder')}
                className="rounded-xl border-[1.5px] border-edge bg-surface p-3 text-base text-ink outline-none focus:border-teal"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={state.busy} className="cursor-pointer rounded-full bg-teal px-5 py-2.5 font-extrabold text-on-teal disabled:opacity-60">
                  {t('spots.submit')}
                </button>
                <button type="button" onClick={() => setDraft(null)} className="cursor-pointer rounded-full border-[1.5px] border-edge px-5 py-2.5 font-extrabold">
                  {t('spots.cancel')}
                </button>
              </div>
            </form>
          ) : (
            <p className="m-0 rounded-3xl bg-paper p-5 font-bold">
              {user ? t('spots.howTo') : (
                <>
                  {t('spots.loginFirst')}{' '}
                  <Link to="/login" state={{ from: '/spots' }} className="text-teal">{t('nav.login')}</Link>
                </>
              )}
            </p>
          )}
          {state.info ? <p role="status" className="m-0 font-bold text-teal">{state.info}</p> : null}
          {state.error ? <p role="alert" className="m-0 font-bold text-live">{state.error}</p> : null}

          <h2 className="m-0 text-xl font-black">{t('spots.top')}</h2>
          <ol className="m-0 flex max-h-[520px] list-none flex-col gap-2 overflow-y-auto p-0">
            {(spots.data ?? []).map((s) => (
              <li
                key={s.id}
                className={`flex items-start gap-3 rounded-2xl border-[1.5px] p-3 ${selected === s.id ? 'border-teal bg-mint' : 'border-line bg-surface'}`}
              >
                <button
                  type="button"
                  disabled={!user}
                  onClick={() => toggleVote(s)}
                  aria-pressed={s.voted}
                  aria-label={t('spots.vote')}
                  className={`flex min-w-12 cursor-pointer flex-col items-center rounded-xl px-2 py-1 font-black disabled:cursor-default ${s.voted ? 'bg-teal text-on-teal' : 'bg-paper text-ink'}`}
                >
                  ▲<span>{s.votes}</span>
                </button>
                <span className="flex flex-col gap-1">
                  <span className="text-[15px]">{s.note}</span>
                  <span className="text-xs font-bold text-subtle">
                    {t(`spots.status.${s.status}`)}
                    {s.admin_note ? ` · ${s.admin_note}` : ''}
                  </span>
                  {isAdmin ? (
                    <select
                      value={s.status}
                      onChange={(e) => admin.updateSpot(s.id, { status: e.target.value }).then(spots.reload)}
                      aria-label={t('spots.setStatus')}
                      className="self-start rounded-lg border border-edge bg-surface px-2 py-1 text-xs font-bold"
                    >
                      {['open', 'planned', 'done', 'rejected'].map((st) => (
                        <option key={st} value={st}>{t(`spots.status.${st}`)}</option>
                      ))}
                    </select>
                  ) : null}
                </span>
              </li>
            ))}
            {spots.data?.length === 0 ? <li className="text-muted">{t('spots.empty')}</li> : null}
          </ol>
        </aside>
      </div>
    </main>
  )
}
